import { formatPhoneNumber, formatPhoneNumberIntl } from 'react-phone-number-input'
import crypto from "crypto-js";
import { HeadCellType, PagingRequest, Permissions, MenuChild } from '../interfaces/common';
import { HEAD_CELL_LABELS_MAPPING, HEAD_CELL_SORT_DISABLED, HEAD_CELL_WIDTH_MAPPING, Menus } from './appConstant';
import { SortOrder } from '../enums';

type headCellModule = keyof typeof HEAD_CELL_LABELS_MAPPING | keyof typeof HEAD_CELL_WIDTH_MAPPING | keyof typeof HEAD_CELL_SORT_DISABLED;

const CRYPTO_SECRET_KEY = process.env.REACT_APP_CRYPTO_SECRET_KEY || ""

export const checkPermissions = (permissions: Permissions | null, accessibleTo: Permissions[]): boolean => {
  if (!permissions) return false;

  return accessibleTo.some((condition) =>
    Object.entries(condition).every(([module, actions]) => {
      const permission = permissions[module as keyof Permissions] || [];
      return actions.some((action) => permission.includes(action));
    })
  );
};

export const hasResourseAccess = (isSystemAdmin: string, permissions: Permissions | null, item: { accessibleTo?: Permissions[]; childrens?: MenuChild[] } | null): boolean => {
  if (isSystemAdmin === 'true') {
    return true;
  }

  if (!item) {
    return true
  }

  if (item.accessibleTo?.length) {
    if (checkPermissions(permissions, item.accessibleTo)) return true;
  }

  if (item.childrens?.length) {
    return item.childrens.some((child) => hasResourseAccess('false', permissions, child));
  }

  return false;
};

export const buildHeadCells = (headCellIds: string[], module: headCellModule) => {
  return headCellIds.map((headCellId) => {
    return {
      id: headCellId,
      sortEnabled: !HEAD_CELL_SORT_DISABLED[module].find((item) => item === headCellId),
      label: HEAD_CELL_LABELS_MAPPING[module][headCellId],
      width: HEAD_CELL_WIDTH_MAPPING[module][headCellId],
    } as HeadCellType;
  });
};

export const sortRowsByAccessor = <T,>(
  rows: T[],
  orderBy: string,
  order: SortOrder,
  accessors: Record<string, (item: T) => string | number | null | undefined>,
): T[] => {
  const accessor = accessors[orderBy];
  if (!accessor) return rows;

  const direction = order === SortOrder.asc ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aValue = accessor(a);
    const bValue = accessor(b);
    const aEmpty = aValue === null || aValue === undefined || aValue === '';
    const bEmpty = bValue === null || bValue === undefined || bValue === '';

    if (aEmpty || bEmpty) {
      if (aEmpty && bEmpty) return 0;
      return aEmpty ? 1 : -1;
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return (aValue - bValue) * direction;
    }

    return String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: 'base' }) * direction;
  });
};

export const getDisplayedMarginAmount = (item: { margin_amount: number }): number => {
  return Number(item.margin_amount || 0);
};

export const formatNumber = (num: number | string): string => {
  return typeof num === "number" ? num.toLocaleString() : num;
}

export const formatAmount = (amount: string, thousandsSeparator = ',') => {
  const num = Number(amount);
  if (isNaN(num)) return '';

  const fixed = num.toFixed(2);
  const [integerPart, decimalPart] = fixed.split('.');

  const withThousands = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

  return `${withThousands}.${decimalPart}`;
};

export const formatDollarAmount = (amount: string): string => {
  const isNegative = amount.startsWith("-");

  const absoluteAmount = isNegative ? amount.slice(1) : amount;
  return isNegative ? `-$${absoluteAmount}` : `$${absoluteAmount}`;
}

export const formatPhone = (countryCode: string, phoneNumber: string): string => {
  // const normalizedCode = countryCode && countryCode.startsWith("+")
  //   ? countryCode
  //   : `+${countryCode}`;

  // const fullNumber = `${normalizedCode}${phoneNumber}`;

  // return formatPhoneNumber(fullNumber);

  const areaCode = phoneNumber.slice(0, 3);
  const prefix = phoneNumber.slice(3, 6);
  const lineNumber = phoneNumber.slice(6);

  return `(${areaCode}) ${prefix}${lineNumber ? '-' + lineNumber : ''}`;
}

export const buildPagingResponse = (payload: PagingRequest) => {
  return {
    page: payload.page,
    per_page: payload.perPage,
    order_by: payload.orderBy,
    order: payload.order
  };
}

export const redirectBasedOnPermission = (isSystemAdmin: string, permissions: Permissions | null, parentMenuId?: string) => {
  let redirectPath = '';

  const parentMenu = Menus.find((item) => item.id === parentMenuId);
  const tempMenus = parentMenuId && parentMenu ? [parentMenu] : Menus;

  tempMenus.forEach((menu) => {
    if (menu.childrens && menu.childrens.length > 0) {
      menu.childrens.forEach((children) => {
        if (hasResourseAccess(isSystemAdmin, permissions, children)) {
          redirectPath = redirectPath || (children.redirectsTo ? children.redirectsTo : '');
        }
      });
    } else {
      if (hasResourseAccess(isSystemAdmin, permissions, menu)) {
        redirectPath = redirectPath || (menu.redirectsTo ? menu.redirectsTo : '');
      }
    }
  });

  return redirectPath;
};

export const encryptData = (data: any): string => {
  try {
    const payload = { value: data };
    const stringData = JSON.stringify(payload);
    return crypto.AES.encrypt(stringData, CRYPTO_SECRET_KEY).toString();
  } catch (error) {
    console.error("Encryption error:", error);
    return "";
  }
};

export const decryptData = (cipherText: string): any => {
  try {
    const bytes = crypto.AES.decrypt(cipherText, CRYPTO_SECRET_KEY);
    const decryptedString = bytes.toString(crypto.enc.Utf8);

    if (!decryptedString) return null;

    const parsed = JSON.parse(decryptedString);
    return parsed.value;
  } catch (error) {
    console.error("Decryption error:", error);
    return null;
  }
};

export const extractDateFromDateTimeBySpace = (dateTime: string) => {
  return dateTime ? dateTime.split(' ')[0] : '';
}

export const formatDate = (d: Date) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const formatDateTimeLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const formatDateLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export const getStartEndOfMonth = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return { startDate: formatDate(start), endDate: formatDate(end) };
};

export const computeVisitDateBounds = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const day = today.getDate();

  // Within 2-day grace period into new month: previous month's dates still allowed
  const minDate = day <= 2
    ? new Date(year, month - 1, 1)
    : new Date(year, month, 1);

  // Max: day 2 of next month (end of grace window)
  const maxDate = new Date(year, month + 1, 2);

  return { minDate: formatDate(minDate), maxDate: formatDate(maxDate) };
};

export const calculateNights = (checkIn: string, checkOut: string): number => {
  const inDateObj = new Date(checkIn);
  const outDateObj = new Date(checkOut);

  const utcIn = Date.UTC(
    inDateObj.getFullYear(),
    inDateObj.getMonth(),
    inDateObj.getDate()
  );

  const utcOut = Date.UTC(
    outDateObj.getFullYear(),
    outDateObj.getMonth(),
    outDateObj.getDate()
  );

  const diffDays = (utcOut - utcIn) / (1000 * 60 * 60 * 24);

  return diffDays > 0 ? diffDays : 1;
}

export const getYearLookupData = () => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 2;
  const endYear = currentYear + 4;
  const years = [];

  for (let year = startYear; year <= endYear; year++) {
    years.push({ id: year.toString(), name: year.toString() });
  }

  return years;
}

export const getCurrentMonthAndYear = () => {
  const now = new Date();
  const year = now.getFullYear().toString();
  const month = (now.getMonth() + 1).toString().padStart(2, '0');

  return { month, year };
}

export const getPreviousMonthAndYear = () => {
  const now = new Date();
  const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const year = previousMonthDate.getFullYear().toString();
  const month = (previousMonthDate.getMonth() + 1).toString().padStart(2, '0');

  return { month, year };
}

export const getInvoiceWarningDateString = () => {
  const now = new Date();
  const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 12, 0, 0));
  utcDate.setDate(utcDate.getDate() - 1);

  const parts = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    timeZone: 'America/Toronto',
  }).formatToParts(utcDate);

  const day = parseInt(parts.find((p) => p.type === 'day')?.value || '0', 10);
  const month = parts.find((p) => p.type === 'month')?.value;
  const suffix = ["th", "st", "nd", "rd"][(day % 100 > 10 && day % 100 < 20) ? 0 : (day % 10 < 4) ? day % 10 : 0];

  return `${day}${suffix} ${month}`;
}